// Renders the composited camera view: the live frame plus a lock-on box per
// face and per animal. Locked faces (a person watching you) are alert red,
// unlocked faces dim cyan; locked animals (a mammal making eye contact) are
// mint green, unlocked animals dim green. We never redraw the video frame
// (the browser composites it for free), only the boxes on top.

import type { FaceAction, TrackedFace } from "../types";
import type { TrackedAnimal } from "../vision/animalTracker";
import { pillFor, toneColor } from "./facePills";

const RED = "#ff2f4a";
const CYAN = "#39d7ff";
const MINT = "#39ffa0";
const DIM_GREEN = "#2f9e73";

export class Overlay {
  private ctx: CanvasRenderingContext2D;

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.ctx = ctx;
  }

  /** Size the backing store to the displayed size (accounting for DPR). */
  resize(cssW: number, cssH: number): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(cssW * dpr);
    this.canvas.height = Math.round(cssH * dpr);
    this.canvas.style.width = `${cssW}px`;
    this.canvas.style.height = `${cssH}px`;
  }

  render(video: HTMLVideoElement, faces: TrackedFace[], animals: TrackedAnimal[] = []): void {
    const { ctx, canvas } = this;
    const cw = canvas.width;
    const ch = canvas.height;
    ctx.clearRect(0, 0, cw, ch);
    if (video.videoWidth === 0) return;

    // The <video> is displayed with object-fit: cover. Map normalized coords
    // through the SAME cover transform so boxes align with the native video — we
    // don't redraw the frame here (the browser composites it for free).
    const scale = Math.max(cw / video.videoWidth, ch / video.videoHeight);
    const dw = video.videoWidth * scale;
    const dh = video.videoHeight * scale;
    const dx = (cw - dw) / 2;
    const dy = (ch - dh) / 2;
    const toPx = (nx: number, ny: number): [number, number] => [dx + nx * dw, dy + ny * dh];

    // People: red when locked (watching you), cyan otherwise.
    for (const t of faces) {
      const [bx, by] = toPx(t.bbox.x, t.bbox.y);
      const bw = t.bbox.w * dw;
      const bh = t.bbox.h * dh;
      const color = t.locked ? RED : CYAN;
      const label = t.locked
        ? `LOCKED · ${(t.lockScore * 100) | 0}%`
        : `${(t.lockScore * 100) | 0}%`;
      this.box(bx, by, bw, bh, color, t.locked, label);
      // 3-word facial-cue pills stacked below a locked face (novelty reading aid).
      if (t.locked && t.actions.length > 0) this.actionPills(bx, by + bh + 4, t.actions);
    }

    // Animals: mint when locked (a mammal making eye contact), dim green otherwise.
    for (const a of animals) {
      const [bx, by] = toPx(a.bbox.x, a.bbox.y);
      const color = a.locked ? MINT : DIM_GREEN;
      const label = a.locked
        ? `🐾 ${a.species.toUpperCase()} · EYES · ${(a.lockScore * 100) | 0}%`
        : `🐾 ${a.species} · ${(a.lockScore * 100) | 0}%`;
      this.box(bx, by, a.bbox.w * dw, a.bbox.h * dh, color, a.locked, label);
    }
  }

  /** Draw one lock-on box with its tag; filled faintly when locked. */
  private box(x: number, y: number, w: number, h: number, color: string, locked: boolean, label: string): void {
    const ctx = this.ctx;
    ctx.lineWidth = locked ? 3 : 1.5;
    ctx.strokeStyle = color;
    this.roundedRect(x, y, w, h, 8);
    ctx.stroke();
    if (locked) {
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.14;
      ctx.fill();
      ctx.globalAlpha = 1;
    }
    this.tag(x, y, label, color);
  }

  /** Draw the 3-word facial-cue pills for one face, stacked downward from (x, y). */
  private actionPills(x: number, y: number, actions: FaceAction[]): void {
    const ctx = this.ctx;
    ctx.font = "600 12px ui-monospace, Menlo, Consolas, monospace";
    let cy = y;
    for (const a of actions) {
      const pill = pillFor(a.name);
      if (!pill) continue;
      const color = toneColor(pill.tone);
      const padX = 7;
      const h = 18;
      const w = ctx.measureText(pill.pill).width + padX * 2;
      // Stronger movement = more opaque pill.
      ctx.globalAlpha = 0.55 + 0.45 * Math.min(1, a.score);
      ctx.fillStyle = "rgba(5,7,12,0.82)";
      this.roundedRect(x, cy, w, h, 9);
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      this.roundedRect(x, cy, w, h, 9);
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.fillText(pill.pill, x + padX, cy + 13);
      ctx.globalAlpha = 1;
      cy += h + 4;
    }
  }

  private roundedRect(x: number, y: number, w: number, h: number, r: number): void {
    const ctx = this.ctx;
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  private tag(x: number, y: number, text: string, color: string): void {
    const ctx = this.ctx;
    ctx.font = "600 13px ui-monospace, Menlo, Consolas, monospace";
    const padX = 6;
    const w = ctx.measureText(text).width + padX * 2;
    const h = 18;
    const ty = y - h - 4 < 0 ? y + 4 : y - h - 4;
    ctx.fillStyle = "rgba(5,7,12,0.72)";
    ctx.fillRect(x, ty, w, h);
    ctx.fillStyle = color;
    ctx.fillText(text, x + padX, ty + 13);
  }
}
